<template>
  <div
    class="compare-index tw-min-h-screen tw-bg-slate-50"
    :style="{ '--compare-scroll-offset': `${scrollOffsetPx}px` }"
  >
    <!-- Sticky header with logo + search (all screen sizes) -->
    <div ref="stickyHeader" class="compare-index__sticky-header">
      <search-hero
        :search-string="searchString"
        :suggestions="suggestions"
        :show-explanations="false"
        compact
        @input-change="onInputChange"
        @selected="onSelected"
        @search="onSearch"
        @go-home="onGoHome"
      />
    </div>

    <!-- Desktop hero -->
    <section class="compare-index__hero tw-hidden lg:tw-block" aria-labelledby="compare-hero-title">
      <div class="compare-index__hero-inner tw-premium-container">
        <p class="compare-index__brand">Compopo</p>
        <h1 id="compare-hero-title" class="compare-index__title">
          {{ $l('compareIndexTitle') }}
        </h1>
        <p class="compare-index__subtitle">
          {{ $l('compareIndexSubtitle') }}
        </p>
      </div>
    </section>

    <!-- Mobile hero -->
    <section class="compare-index__hero-mobile lg:tw-hidden" aria-labelledby="compare-hero-title-mobile">
      <div class="tw-premium-container tw-px-4 tw-pb-4 tw-pt-2">
        <h1 id="compare-hero-title-mobile" class="compare-index__title compare-index__title--mobile">
          {{ $l('compareIndexTitle') }}
        </h1>
        <p class="compare-index__subtitle compare-index__subtitle--mobile">
          {{ $l('compareIndexSubtitle') }}
        </p>
      </div>
    </section>

    <!-- Sticky category nav -->
    <nav
      ref="categoryNav"
      class="compare-index__category-nav"
      :style="categoryNavStyle"
      role="navigation"
      :aria-label="$l('categories')"
    >
      <div class="compare-index__category-nav-inner tw-premium-container">
        <button
          v-for="group in categoryGroups"
          :key="group.key"
          type="button"
          class="compare-index__nav-item"
          :class="{ 'compare-index__nav-item--active': activeSection === group.key }"
          @click="scrollToCategory(group.key)"
        >
          {{ navLabel(group) }}
        </button>
      </div>
    </nav>

    <div class="tw-premium-container tw-pb-10 lg:tw-pb-14">
      <section
        v-for="group in categoryGroups"
        :id="sectionId(group.key)"
        :key="group.key"
        class="compare-index__section"
        :class="{ 'compare-index__section--categories': group.key === 'categories' }"
      >
        <h2 class="compare-index__section-title">
          {{ categoryLabel(group) }}
        </h2>
        <div
          class="compare-index__grid"
          :class="{ 'compare-index__grid--categories': group.key === 'categories' }"
        >
          <router-link
            v-for="page in group.pages"
            :key="page.slug"
            :to="`/compare/${page.slug}`"
            class="compare-index__card-link"
          >
            <article
              class="compare-index__card"
              :class="`compare-index__card--${group.key}`"
            >
              <div class="compare-index__card-accent" aria-hidden="true" />
              <div class="compare-index__card-body">
                <span class="compare-index__card-badge">{{ categoryLabel(group) }}</span>
                <h3 class="compare-index__card-title">{{ pageTitle(page) }}</h3>
                <p class="compare-index__card-desc">{{ pageDescription(page) }}</p>
                <span class="compare-index__card-cta">
                  {{ $l('compareIndexCompareCta') }}
                  <span aria-hidden="true">→</span>
                </span>
              </div>
            </article>
          </router-link>
        </div>
      </section>
    </div>

    <main-footer />
  </div>
</template>

<script>
import axios from 'axios';
import MainFooter from '../layout/MainFooter.vue';
import SearchHero from './components/SearchHero.vue';
import { getComparePagesByCategory, SITE_URL } from '@/seo/comparePages';
import { setPageMeta } from '@/seo/seoUtils';

const NAV_LABEL_KEYS = {
  gaming: 'compareIndexTabGaming',
  phones: 'compareIndexTabPhones',
  electronics: 'compareIndexTabHome',
  audio: 'compareIndexTabAudio',
  computing: 'compareIndexTabComputing',
  fashion: 'compareIndexTabFashion',
  sports: 'compareIndexTabSports',
  beauty: 'compareIndexTabBeauty',
  baby: 'compareIndexTabBaby',
  pets: 'compareIndexTabPets',
  kitchen: 'compareIndexTabKitchen',
  tools: 'compareIndexTabTools',
  watches: 'compareIndexTabWatches',
  toys: 'compareIndexTabToys',
  categories: 'compareIndexTabMore',
};

export default {
  name: 'CompareIndex',
  components: {
    MainFooter,
    SearchHero,
  },
  data() {
    return {
      searchString: '',
      suggestions: [{ data: [] }],
      awaitingSearch: false,
      activeSection: 'gaming',
      stickyHeaderHeight: 0,
      categoryNavHeight: 0,
      sectionObserver: null,
    };
  },
  computed: {
    categoryGroups() {
      return getComparePagesByCategory();
    },
    categoryNavStyle() {
      return { top: `${this.stickyHeaderHeight}px` };
    },
    scrollOffsetPx() {
      return this.stickyHeaderHeight + this.categoryNavHeight + 8;
    },
  },
  watch: {
    locale() {
      this.updateSeo();
    },
    '$route.hash'(hash) {
      const key = (hash || '').replace('#', '');
      if (!key || !this.categoryGroups.some((g) => g.key === key)) return;
      this.$nextTick(() => {
        setTimeout(() => this.scrollToCategory(key, false), 100);
      });
    },
  },
  mounted() {
    this.updateSeo();
    const seoPrerender = document.getElementById('seo-prerender');
    if (seoPrerender) {
      seoPrerender.remove();
    }

    this.updateNavLayout();
    window.addEventListener('resize', this.updateNavLayout);
    this.$nextTick(() => {
      if (this.$route.hash) {
        const key = this.$route.hash.replace('#', '');
        if (this.categoryGroups.some((g) => g.key === key)) {
          setTimeout(() => this.scrollToCategory(key, false), 150);
        }
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateNavLayout);
    if (this.sectionObserver) {
      this.sectionObserver.disconnect();
    }
  },
  methods: {
    sectionId(key) {
      return key;
    },
    categoryLabel(group) {
      return this.locale === 'he' ? group.labelHe : group.labelEn;
    },
    navLabel(group) {
      const key = NAV_LABEL_KEYS[group.key];
      return key ? this.$l(key) : this.categoryLabel(group);
    },
    pageTitle(page) {
      return this.locale === 'he' ? page.titleHe : page.titleEn;
    },
    pageDescription(page) {
      return this.locale === 'he' ? page.descriptionHe : page.descriptionEn;
    },
    updateNavLayout() {
      this.$nextTick(() => {
        const header = this.$refs.stickyHeader;
        const nav = this.$refs.categoryNav;
        this.stickyHeaderHeight = header ? header.offsetHeight : 0;
        this.categoryNavHeight = nav ? nav.offsetHeight : 0;
        this.setupSectionObserver();
      });
    },
    scrollOffset() {
      return this.scrollOffsetPx || 140;
    },
    scrollToCategory(key, updateHash = true) {
      const el = document.getElementById(this.sectionId(key));
      if (!el) return;

      this.activeSection = key;
      const top = el.getBoundingClientRect().top + window.scrollY - this.scrollOffset();
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

      if (updateHash && this.$route.hash !== `#${key}`) {
        this.$router.replace({ hash: `#${key}` }).catch(() => {});
      }
    },
    setupSectionObserver() {
      if (this.sectionObserver) {
        this.sectionObserver.disconnect();
      }

      const options = {
        root: null,
        rootMargin: `-${this.scrollOffset()}px 0px -55% 0px`,
        threshold: 0,
      };

      this.sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.activeSection = entry.target.id;
          }
        });
      }, options);

      this.categoryGroups.forEach((group) => {
        const el = document.getElementById(this.sectionId(group.key));
        if (el) {
          this.sectionObserver.observe(el);
        }
      });
    },
    updateSeo() {
      const isHe = this.locale === 'he';
      setPageMeta({
        title: isHe
          ? 'השוואות מחירים פופולריות | Compopo'
          : 'Popular Price Comparisons | Compopo',
        description: isHe
          ? 'השוו מחירים על מוצרים פופולריים מ-eBay, AliExpress ו-Amazon. PS5 Pro, Nintendo Switch 2, iPhone ועוד.'
          : 'Compare prices on popular products from eBay, AliExpress and Amazon. PS5 Pro, Nintendo Switch 2, iPhone and more.',
        canonical: `${SITE_URL}/compare`,
        locale: this.locale,
      });
    },
    onGoHome() {
      this.$router.push('/');
    },
    onSearch() {
      if (this.searchString.trim()) {
        this.$router.push({ path: '/', query: { search: this.searchString.trim() } });
      }
    },
    onSelected(item) {
      if (item && item.item) {
        this.searchString = item.item;
        this.onSearch();
      }
    },
    onInputChange(text) {
      this.searchString = text;
      if (!this.awaitingSearch) {
        setTimeout(() => {
          if (text !== '') {
            this.sendQueryForCompletion(text);
          }
          this.awaitingSearch = false;
        }, 100);
      }
      this.awaitingSearch = true;
    },
    sendQueryForCompletion(text) {
      axios({
        method: 'post',
        url: '/api/v1/getAutoComplete/',
        data: { searchString: text },
      })
        .then((res) => {
          this.suggestions = [{ data: res.data || [] }];
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.compare-index__sticky-header {
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(226, 232, 240, 0.9);
  box-shadow: 0 1px 8px rgba(15, 23, 42, 0.06);
}

.compare-index__hero {
  background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 45%, #f0f9ff 100%);
  border-bottom: 1px solid rgba(226, 232, 240, 0.8);
}

.compare-index__hero-inner {
  padding: 3.5rem 1rem 3rem;
  text-align: center;
}

.compare-index__brand {
  margin: 0 0 0.75rem;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #2563eb;
}

.compare-index__title {
  margin: 0;
  font-size: clamp(2.5rem, 4vw, 3.5rem);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: #0f172a;
}

.compare-index__title--mobile {
  font-size: 1.625rem;
  text-align: center;
}

.compare-index__subtitle {
  margin: 1.25rem auto 0;
  max-width: 36rem;
  font-size: 1.25rem;
  line-height: 1.5;
  color: #475569;
}

.compare-index__subtitle--mobile {
  margin-top: 0.5rem;
  font-size: 0.9375rem;
  text-align: center;
}

.compare-index__sticky-header ::v-deep .search-hero {
  background: transparent !important;
  border-bottom: none !important;
}

.compare-index__sticky-header ::v-deep .search-hero.tw-sticky-header {
  position: static;
  box-shadow: none;
}

.compare-index__category-nav {
  position: sticky;
  z-index: 40;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.06);
}

.compare-index__category-nav-inner {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding: 0.75rem 1rem;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.compare-index__category-nav-inner::-webkit-scrollbar {
  display: none;
}

.compare-index__nav-item {
  flex-shrink: 0;
  border: 1px solid #e2e8f0;
  border-radius: 9999px;
  background: #fff;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s ease;
}

.compare-index__nav-item:hover {
  border-color: #93c5fd;
  color: #1d4ed8;
}

.compare-index__nav-item--active {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.15);
}

.compare-index__section {
  margin-bottom: 3rem;
  scroll-margin-top: var(--compare-scroll-offset, 6rem);
}

.compare-index__section--categories {
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.compare-index__section-title {
  margin: 0 0 1.25rem;
  font-size: 1.625rem;
  font-weight: 700;
  color: #0f172a;
}

.compare-index__grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.compare-index__grid--categories {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.compare-index__card-link {
  text-decoration: none;
  color: inherit;
  display: block;
  height: 100%;
}

.compare-index__card {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border-radius: 1rem;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.compare-index__card-link:hover .compare-index__card,
.compare-index__card-link:focus-visible .compare-index__card {
  transform: translateY(-2px);
  border-color: #93c5fd;
  box-shadow: 0 8px 24px rgba(37, 99, 235, 0.12);
}

.compare-index__card-accent {
  height: 4px;
  width: 100%;
}

.compare-index__card--gaming .compare-index__card-accent {
  background: linear-gradient(90deg, #7c3aed, #a78bfa);
}

.compare-index__card--phones .compare-index__card-accent {
  background: linear-gradient(90deg, #2563eb, #60a5fa);
}

.compare-index__card--electronics .compare-index__card-accent,
.compare-index__card--categories .compare-index__card-accent {
  background: linear-gradient(90deg, #059669, #34d399);
}

.compare-index__card--audio .compare-index__card-accent {
  background: linear-gradient(90deg, #db2777, #f472b6);
}

.compare-index__card--computing .compare-index__card-accent {
  background: linear-gradient(90deg, #0891b2, #22d3ee);
}

.compare-index__card--fashion .compare-index__card-accent {
  background: linear-gradient(90deg, #c026d3, #f0abfc);
}

.compare-index__card--sports .compare-index__card-accent {
  background: linear-gradient(90deg, #ea580c, #fb923c);
}

.compare-index__card--beauty .compare-index__card-accent {
  background: linear-gradient(90deg, #e11d48, #fb7185);
}

.compare-index__card--baby .compare-index__card-accent {
  background: linear-gradient(90deg, #0d9488, #5eead4);
}

.compare-index__card--pets .compare-index__card-accent {
  background: linear-gradient(90deg, #ca8a04, #facc15);
}

.compare-index__card--kitchen .compare-index__card-accent {
  background: linear-gradient(90deg, #dc2626, #f87171);
}

.compare-index__card--tools .compare-index__card-accent {
  background: linear-gradient(90deg, #4b5563, #9ca3af);
}

.compare-index__card--watches .compare-index__card-accent {
  background: linear-gradient(90deg, #1d4ed8, #60a5fa);
}

.compare-index__card--toys .compare-index__card-accent {
  background: linear-gradient(90deg, #7c3aed, #c4b5fd);
}

.compare-index__card-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 1.25rem;
}

.compare-index__card-badge {
  display: inline-block;
  margin-bottom: 0.625rem;
  border-radius: 9999px;
  background: #f1f5f9;
  padding: 0.25rem 0.625rem;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.compare-index__card-title {
  margin: 0 0 0.5rem;
  font-size: 1.0625rem;
  font-weight: 700;
  line-height: 1.35;
  color: #0f172a;
}

.compare-index__card-desc {
  margin: 0;
  flex: 1;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #64748b;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.compare-index__card-cta {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 1rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #2563eb;
}

@media (max-width: 1279px) {
  .compare-index__grid,
  .compare-index__grid--categories {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 1023px) {
  .compare-index__grid,
  .compare-index__grid--categories {
    grid-template-columns: 1fr;
  }
}
</style>
